import { supabase } from "../../config/supabase";

export interface Company {
  id: string;
  company_name: string;
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  telephone?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface CompanyData {
  company_name: string;
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  telephone?: string;
}

export class CompanyService {
  async listCompanies() {
    try {
      // Get current user for RLS policy
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("user_id", user.id)
        .order("company_name", { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: (data as Company[]).map((company) => ({
          id: company.id,
          company_name: company.company_name,
          street: company.street,
          city: company.city,
          province: company.province,
          country: company.country,
          telephone: company.telephone,
        })),
      };
    } catch (error) {
      console.error("Error listing companies:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: [],
      };
    }
  }

  async createCompany(companyData: CompanyData) {
    try {
      // Get current user for RLS policy
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      // Generate a unique company_id
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);

      const { data, error } = await supabase
        .from("companies")
        .insert({
          company_name: companyData.company_name,
          street: companyData.street,
          city: companyData.city,
          province: companyData.province,
          country: companyData.country || "IT",
          telephone: companyData.telephone,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: (data as Company).id,
          company_name: (data as Company).company_name,
          street: (data as Company).street,
          city: (data as Company).city,
          province: (data as Company).province,
          country: (data as Company).country,
          telephone: (data as Company).telephone,
        },
      };
    } catch (error) {
      console.error("Error creating company:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: null,
      };
    }
  }

  async updateCompany(id: string, companyData: CompanyData) {
    try {
      const { data, error } = await supabase
        .from("companies")
        .update({
          company_name: companyData.company_name,
          street: companyData.street,
          city: companyData.city,
          province: companyData.province,
          country: companyData.country || "IT",
          telephone: companyData.telephone,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: (data as Company).id,
          company_name: (data as Company).company_name,
          street: (data as Company).street,
          city: (data as Company).city,
          province: (data as Company).province,
          country: (data as Company).country,
          telephone: (data as Company).telephone,
        },
      };
    } catch (error) {
      console.error("Error updating company:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: null,
      };
    }
  }

  async deleteCompany(id: string) {
    try {
      const { error } = await supabase.from("companies").delete().eq("id", id);

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error deleting company:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async getCompaniesByUserId(userId: string) {
    try {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("user_id", userId)
        .order("company_name", { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: (data as Company[]).map((company) => ({
          id: company.id,
          company_name: company.company_name,
          street: company.street,
          city: company.city,
          province: company.province,
          country: company.country,
          telephone: company.telephone,
        })),
      };
    } catch (error) {
      console.error(
        "Error getting companies by user ID:",
        (error as Error).message
      );
      return {
        success: false,
        error: (error as Error).message,
        data: [],
      };
    }
  }

  async existsCompanyByName(company_name: string) {
    try {
      const { data, error } = await supabase
        .from("companies")
        .select("id")
        .ilike("company_name", company_name);
      if (error) {
        console.error("Error checking company by name:", error);
        return { success: false, error };
      }
      return { success: true, exists: Array.isArray(data) && data.length > 0 };
    } catch (error) {
      console.error("Error in existsCompanyByName:", error);
      return { success: false, error };
    }
  }
}

export const companyService = new CompanyService();
